/**
 * Shared Zod schemas for bouquet create/update flows.
 *
 * Single source of truth for FE form validation and BE request validation,
 * eliminating schema drift that previously forced `as any` casts.
 *
 * Pattern:
 *   bouquetFormSchema   — all optional, represents the in-progress form state
 *   createBouquetSchema — required fields for creation (name, price, flowers, categoryIds)
 *   updateBouquetSchema — partial of the form schema (all optional)
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Primitive building blocks
// ---------------------------------------------------------------------------
const nonEmptyString = z.string().trim().min(1);
const optionalTrimmedString = z.string().trim().optional();
const optionalStringArray = z.array(z.string().trim()).optional();
// ---------------------------------------------------------------------------
// Bouquet form schema (all optional — represents in-progress form state)
// ---------------------------------------------------------------------------
export const bouquetFormSchema = z.object({
    // Identity
    name: optionalTrimmedString,
    nameEn: optionalTrimmedString,
    nameAz: optionalTrimmedString,
    slug: optionalTrimmedString,
    // Money
    price: z.number().nonnegative().optional(),
    // Media
    image: optionalTrimmedString,
    images: optionalStringArray,
    imagePositionX: z.number().min(0).max(100).optional(),
    imagePositionY: z.number().min(0).max(100).optional(),
    // Categories (multi-category; legacy single categoryId kept for BC)
    categoryId: optionalTrimmedString,
    categoryIds: z.array(nonEmptyString).optional(),
    // Flowers (composition)
    flowers: z
        .array(z.object({
        variantId: nonEmptyString,
        quantity: z.number().int().positive(),
    }))
        .optional(),
    // Colors / meta
    colors: optionalStringArray,
    occasion: optionalTrimmedString,
    // Description
    description: optionalTrimmedString,
    // Flags
    isActive: z.boolean().optional(),
    isDraft: z.boolean().optional(),
    inStock: z.boolean().optional(),
    stockCount: z.number().int().nonnegative().optional(),
    isPopular: z.boolean().optional(),
    isNew: z.boolean().optional(),
    // Showcase / preorder
    type: z.enum(['CATALOG', 'SHOWCASE']).optional(),
    deliverySlaMinutes: z.number().int().nonnegative().optional(),
    showcaseStatus: z.enum(['ACTIVE', 'SOLD', 'ARCHIVED']).optional(),
    showcaseReason: optionalTrimmedString,
    // SEO (AI-generated)
    seoTitleRu: optionalTrimmedString,
    seoTitleEn: optionalTrimmedString,
    seoTitleAz: optionalTrimmedString,
    seoDescriptionRu: optionalTrimmedString,
    seoDescriptionEn: optionalTrimmedString,
    seoDescriptionAz: optionalTrimmedString,
    seoKeywordsRu: optionalStringArray,
    seoKeywordsEn: optionalStringArray,
    seoKeywordsAz: optionalStringArray,
    // Intent
    intentCategory: optionalTrimmedString,
    // AI artefacts (JSON blobs — validated elsewhere)
    assistantFacts: z.record(z.string(), z.unknown()).optional(),
    visionFacts: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Derived schemas
// ---------------------------------------------------------------------------
/**
 * Required for create: name, price, flowers (≥1), and at least one category.
 * Enforced via refine so field-specific error messages map to form fields.
 */
export const createBouquetSchema = bouquetFormSchema
    .extend({
    name: nonEmptyString,
    price: z.number().nonnegative(),
    image: nonEmptyString,
})
    .superRefine((data, ctx) => {
    const isDraft = data.isDraft === true;
    if (isDraft)
        return;
    if (!data.flowers || data.flowers.length < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['flowers'],
            message: 'Bouquet must have at least one flower',
        });
    }
    const hasCategory = (data.categoryIds && data.categoryIds.length > 0) || Boolean(data.categoryId);
    if (!hasCategory) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['categoryIds'],
            message: 'Bouquet must have at least one category',
        });
    }
});
export const updateBouquetSchema = bouquetFormSchema.partial();
